import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #earlyOpenTimeout = null;
  #errorTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:add-start', this.#handleCartAddStart);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:add-start', this.#handleCartAddStart);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
  }

  #handleCartAddStart = () => {
    if (!this.hasAttribute('auto-open')) return;
    // Otevřít drawer po 400ms pokud API ještě neodpovědělo
    this.#earlyOpenTimeout = setTimeout(() => {
      this.showDialog();
    }, 400);
  };

  #handleCartAdd = () => {
    // API odpovědělo — zrušit early open timeout a otevřít normálně
    if (this.#earlyOpenTimeout) {
      clearTimeout(this.#earlyOpenTimeout);
      this.#earlyOpenTimeout = null;
    }
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleCartError = () => {
    const errorEl = this.querySelector('[ref="cartDrawerError"]');
    const errorText = this.querySelector('[ref="cartDrawerErrorText"]');
    if (!errorEl || !errorText) return;

    errorText.textContent = Theme.translations.cart_quantity_error || 'You have exceeded the maximum quantity for this product.';
    errorEl.classList.remove('hidden');

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => {
      errorEl.classList.add('hidden');
    }, 10000);
  };

  open() {
    this.showDialog();
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * Self-initializing custom element for the cart recommended products slider.
 * connectedCallback fires automatically when morphSection injects new HTML,
 * fixing the bug where inline scripts don't run on dynamic DOM updates.
 */
class CartRecommendedSliderComponent extends HTMLElement {
  #slider = null;
  #progressBar = null;
  #prevBtn = null;
  #nextBtn = null;
  #scrollRafId = null;

  /**
   * Cart refreshes morph the slider in place: cards change and the progress bar gets its
   * server style back. Recalculate afterwards; #setStyle skips unchanged values, so the
   * observer does not trigger itself again.
   */
  #morphObserver = new MutationObserver(() => this.#onScroll());

  connectedCallback() {
    this.#slider = this.querySelector('[data-slider]');
    this.#progressBar = this.querySelector('[data-progress-bar]');
    this.#prevBtn = this.querySelector('[data-arrow-prev]');
    this.#nextBtn = this.querySelector('[data-arrow-next]');

    if (!this.#slider) return;

    this.#slider.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#prevBtn?.addEventListener('click', this.#onPrev);
    this.#nextBtn?.addEventListener('click', this.#onNext);
    document.addEventListener('cart-drawer:open', this.#onDrawerOpen);
    this.#morphObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });

    this.#updateProgress();
  }

  disconnectedCallback() {
    this.#slider?.removeEventListener('scroll', this.#onScroll);
    this.#prevBtn?.removeEventListener('click', this.#onPrev);
    this.#nextBtn?.removeEventListener('click', this.#onNext);
    document.removeEventListener('cart-drawer:open', this.#onDrawerOpen);
    this.#morphObserver.disconnect();
    if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);
  }

  /**
   * @param {HTMLElement | null} element
   * @param {'width' | 'marginLeft' | 'opacity'} property
   * @param {string} value
   */
  #setStyle(element, property, value) {
    if (element && element.style[property] !== value) element.style[property] = value;
  }

  #onDrawerOpen = () => {
    setTimeout(() => this.#updateProgress(), 100);
  };

  #onScroll = () => {
    if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);
    this.#scrollRafId = requestAnimationFrame(() => this.#updateProgress());
  };

  #onPrev = () => {
    this.#slider?.scrollBy({ left: -this.#scrollStep(), behavior: 'smooth' });
  };

  #onNext = () => {
    this.#slider?.scrollBy({ left: this.#scrollStep(), behavior: 'smooth' });
  };

  /** One card and its gap. The cross-sell keeps cards of other shuffle seeds hidden, so skip those. */
  #scrollStep() {
    const cards = this.#slider?.querySelectorAll('.cart-recommended__card') ?? [];
    const card = Array.from(cards).find((element) => element instanceof HTMLElement && element.offsetWidth > 0);
    return card instanceof HTMLElement ? card.offsetWidth + 8 : 148;
  }

  #updateProgress() {
    const slider = this.#slider;
    const bar = this.#progressBar;
    if (!slider || !bar) return;

    const maxScroll = slider.scrollWidth - slider.clientWidth;
    if (maxScroll <= 0) {
      this.#setStyle(bar, 'width', '100%');
      this.#setStyle(bar, 'marginLeft', '0px');
      this.#setStyle(this.#prevBtn, 'opacity', '0.3');
      this.#setStyle(this.#nextBtn, 'opacity', '0.3');
      return;
    }

    const ratio = slider.scrollLeft / maxScroll;
    const viewRatio = slider.clientWidth / slider.scrollWidth;
    const barWidth = Math.max(viewRatio * 100, 10);
    const barLeft = ratio * (100 - barWidth);
    // Rounded like the browser serializes them, so unchanged values compare equal.
    this.#setStyle(bar, 'width', `${Number(barWidth.toFixed(2))}%`);
    this.#setStyle(bar, 'marginLeft', `${Number(barLeft.toFixed(2))}%`);

    this.#setStyle(this.#prevBtn, 'opacity', slider.scrollLeft <= 0 ? '0.3' : '1');
    this.#setStyle(this.#nextBtn, 'opacity', slider.scrollLeft >= maxScroll - 1 ? '0.3' : '1');
  }
}

if (!customElements.get('cart-recommended-slider')) {
  customElements.define('cart-recommended-slider', CartRecommendedSliderComponent);
}
